<template>
  <div>
    <table :class="$style.ruler">
      <tr class="ruler">
        <td v-for="number in hoursNumber.hours" :key="number" :class="$style.hour">
          {{ getRulerTime(number) }}
        </td>
      </tr>
    </table>
    <div :class="[$style.timeline, { [$style.timelineWt]: isWorktimeDetails }]">
      <svg :class="$style.svg">
        <g v-for="(event, index) in timeline" :key="event.start + index">
          <text v-if="index === 0" :class="$style.text" :x="event.start - 6 + '%'" y="15">
            {{ getTime(date.dayStart) }}
          </text>
          <rect
            :width="event.width + '%'"
            :x="event.start + '%'"
            :class="[
              $style.bar,
              {
                [$style.red]: event.productivity === 0,
              },
              {
                [$style.green]: event.productivity === 1,
              },
              {
                [$style.yellow]: event.productivity === 2,
              },
              {
                [$style.white]: event.productivity === 4,
              },
            ]"
          />
          <title :class="$style.tooltiptext">
            {{ convertSecondsToDays(event.width / oneSecondPercent) }}
          </title>
          <text
            v-if="index === timeline.length - 1"
            :class="$style.text"
            :x="event.start + event.width + 0.5 + '%'"
            y="15"
          >
            {{ getTime(date.dayFinish) }}
          </text>
        </g>
      </svg>
      <svg v-if="isWorktimeDetails" :class="[$style.svg, $style.activityLog]">
        <rect :class="$style.lineLog" />
        <g v-for="event in activityLog" :key="event.id" :class="$style.itemLog">
          <rect
            :class="$style.lineItem"
            :x="`${event.start}%`"
            y="3"
            :style="{
              height: `${event.height}%`,
              fill: getColor(event),
            }"
            @mouseover="showEventInfo(event)"
            @mouseleave="hideEventInfo()"
            @mousemove="mouseMove"
          />
        </g>
      </svg>
      <div
        v-if="currentEvent"
        :class="$style.infoContainer"
        :style="{
          top: mouseY + 'px',
          left: mouseX + 'px',
        }"
      >
        {{ currentEvent.actType }}
      </div>
    </div>
  </div>
</template>

<script>
import { getTime, convertSecondsToDays } from '@/helpers/format-date';
import { ROUTE_WORKTIME_DETAILS } from '@/views/reports/const/const';

export default {
  props: ['columns', 'date'],
  data: () => ({
    timeline: [],
    activityLog: [],
    currentEvent: {},
    mouseX: 0,
    mouseY: 0,
  }),
  computed: {
    isWorktimeDetails() {
      return this.$route.name === ROUTE_WORKTIME_DETAILS;
    },
    hoursNumber() {
      const startTime = new Date(this.date.dayStart).getUTCHours() - 1;
      const finishTime = new Date(this.date.dayFinish).getUTCHours() + 1;
      const hoursNumber = finishTime - startTime + 1;
      const hoursArray = [];
      for (let i = startTime; i <= finishTime; i++) {
        hoursArray.push(i);
      }
      return {
        start: startTime,
        finish: finishTime,
        number: hoursNumber,
        hours: hoursArray,
      };
    },
    oneSecondPercent() {
      return 1 / (this.hoursNumber.number * 36);
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    getColor(event) {
      if (this.currentEvent?.id === event.id) return '#d49a02';
      const base = { r: 163, g: 213, b: 247 };

      const maxLighten = 0.8;
      const factor = (event.height / 100) * maxLighten;

      const r = Math.round(base.r - (255 - base.r) * factor);
      const g = Math.round(base.g - (255 - base.g) * factor);
      const b = Math.round(base.b - (255 - base.b) * factor);

      return `rgb(${r}, ${g}, ${b})`;
    },
    mouseMove(event) {
      this.mouseX = event.clientX + 10;
      this.mouseY = event.clientY - 20;
    },
    showEventInfo(event) {
      this.currentEvent = event;
    },
    hideEventInfo() {
      this.currentEvent = null;
    },
    getRulerTime(hours) {
      if (hours === 24) return '00:00';
      if (hours === -1) return '23:00';
      return `${hours}:00`;
    },
    convertSecondsToDays(secNum) {
      return convertSecondsToDays(secNum);
    },
    getTime(date) {
      return getTime(date);
    },

    getStartTime(date) {
      const startTime = new Date(date);
      const startHour = startTime.getUTCHours() - this.hoursNumber.start;
      const startMinute = startTime.getMinutes();
      const startSecond = startTime.getSeconds();

      return (startHour * 3600 + startMinute * 60 + startSecond) * this.oneSecondPercent;
    },

    fetch() {
      const { timeline, activityLog, maxValue } = this.date;
      if (!timeline?.length) return;

      timeline.forEach(({ duration, eventStart, productivity }) => {
        if (duration < 0) duration = 0;
        this.timeline.push({
          width: duration * this.oneSecondPercent,
          start: this.getStartTime(eventStart),
          productivity,
        });
      });

      if (!activityLog?.length) return;

      activityLog.forEach(({ dateTime, actType, id }) => {
        this.activityLog.push({
          start: this.getStartTime(dateTime),
          actType,
          height: (actType / maxValue) * 100,
          id,
        });
      });
    },
  },
};
</script>

<style lang="scss" module>
  .ruler {
    width: 100%;
    margin-bottom: 20px;
    table-layout: fixed;
  }
  .hour {
    border: 1px solid $borderColor;
    height: 60px;
    color: #7e7e7e;
    font-weight: 300;
    font-size: 12px;
    text-align: left;
    padding-left: 7px;
    padding-top: 7px;
  }
  .timeline {
    margin-top: -52px;
    height: 40px;
    width: 100%;
  }
  .timelineWt {
    height: 100px;
  }
  .svg {
    width: 100%;
    height: 50px;
    overflow: visible;
  }
  .bar {
    height: 23px;
    cursor: pointer;
    &.red {
      fill: #ffb0b0;

      &:hover {
        fill: $red;
      }
    }
    &.green {
      fill: #7cd890;

      &:hover {
        fill: #1f8d37;
      }
    }
    &.yellow {
      fill: #ffecbc;

      &:hover {
        fill: #d49a02;
      }
    }
    &.white {
      fill: #e8e8e8;

      &:hover {
        fill: $branchColor;
      }
    }
  }

  .tooltip .tooltiptext {
    visibility: hidden;
    width: 120px;
    background-color: black;
    color: #fff;
    text-align: center;
    border-radius: 6px;
    padding: 5px 0;

    /* Position the tooltip */
    position: absolute;
    z-index: 1;
  }

  .tooltip:hover .tooltiptext {
    visibility: visible;
  }
  .text {
    font-size: 12px;
  }
  .activityLog {
    transform: scale(1, -1);
  }
  .lineLog {
    height: 3px;
    width: 100%;
    fill: #e9f6ff;
  }
  .itemLog {
    cursor: pointer;
  }
  .lineItem {
    width: 3px;
    fill: #e9f6ff;
    &:hover {
      fill: #d49a02;
    }
  }

  .infoContainer {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    z-index: 1000;
    border-radius: 1000px;
    width: 20px;
    height: 20px;
    background-color: #fff;
    color: #d49a02;
  }
</style>
